import { Dispatch, useEffect, useMemo } from 'react';

import { ChakraBox, ChakraFlex } from '@equip.health/ui';
import { DateTime } from 'luxon';
import SectionHeader from '~/components/common/SectionHeader';
import { InsightsOverview } from '~/components/maud/patients/profile/tabs/overviews/InsightsOverview';
import PatientOverviewRightRail from '~/components/maud/patients/profile/tabs/overviews/PatientOverviewRightRail';

import { CalendarListTypeOption } from '~/lib/constants/appointment.constants';
import {
  AppointmentStatus,
  OVERVIEW_TAB_APPOINTMENTS_COUNT,
} from '~/lib/constants/calendar.constants';
import {
  AssessmentAction,
  PatientTabs,
} from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import { deriveScheduleItems } from '~/lib/util/schedule/schedule.util';
import { CompServTask } from '~/lib/util/task.util';
import { toISO } from '~/lib/utils';
import { useLazyGetGroupClassEventsQuery } from '~/store/services/groupClass';
import UpcomingEvents from './UpcomingEvents';
import { PatientTasksOverview } from '../common/PatientTasks';
import PatientSurveys from '../surveys';
import { useGetAppointmentDetailsQuery } from '~/store/services/appointment';
import { useFlags } from 'launchdarkly-react-client-sdk';

// V1 of the patient Overview tab, rendered when the mai-chat-panel flag is off.
// The MAI team is actively building PatientOverviewTabV2 to replace this via the
// PatientOverviewTabSwitcher. Before starting any work on V1, reach out to the
// MAI team first (#mai-dev-xfn) — changes here may conflict with or be obsoleted by V2.
export interface PatientOverviewTabV1Props {
  assessmentList: SurveyResponse[];
  detailsTabId?: string;
  isAssessmentError: boolean;
  isAssessmentLoading: boolean;
  onSendEmail: (assessment: Partial<SurveyResponse>) => void;
  onUpdateAssessmentStatus: (
    status: SurveyResponseStatus,
    toUserEmail: string,
    responseExternalId: string,
  ) => void;
  openTasks?: CompServTask[];
  patientFirstName: string;
  patientId: string;
  patientLastName: string;
  patientTasks?: Task[];
  selectedSurveyResults: Partial<SurveyResponse>;
  surveys: Survey[];
  updateAssessmentState: Dispatch<AssessmentAction>;
}
const PatientOverviewTabV1 = ({
  assessmentList,
  detailsTabId,
  isAssessmentError,
  isAssessmentLoading,
  openTasks,
  patientFirstName,
  patientId,
  patientLastName,
  patientTasks = [],
  updateAssessmentState,
  surveys,
  onSendEmail,
  onUpdateAssessmentStatus,
  selectedSurveyResults,
}: PatientOverviewTabV1Props) => {
  const now = useMemo(() => DateTime.now(), []);

  const {
    viewCancelledAppointmentsInMaud: isViewCancelledAppointmentsInMaudEnabled,
  } = useFlags();

  const { data: appointmentDetails, isLoading: appointmentsLoading } =
    useGetAppointmentDetailsQuery(
      {
        startDateISO: now.minus({ minutes: 90 }).toISO(),
        patientId,
        statuses: [AppointmentStatus.SCHEDULED],
        pageSize: OVERVIEW_TAB_APPOINTMENTS_COUNT * 3,
      },
      { skip: !patientId },
    );

  const [
    getPatientGroupClassEvents,
    {
      data: patientGroupClassEvents,
      isLoading: isGetPatientGroupClassEventsLoading,
    },
  ] = useLazyGetGroupClassEventsQuery();

  const upcomingEvents = useMemo<ScheduleItemBase[]>(() => {
    return deriveScheduleItems(
      patientId,
      appointmentDetails?.data,
      patientGroupClassEvents?.data,
      [],
      isViewCancelledAppointmentsInMaudEnabled,
    )
      .filter(
        (item) =>
          DateTime.fromISO(item.startDateTime).diffNow().milliseconds > 0,
      )
      .slice(0, OVERVIEW_TAB_APPOINTMENTS_COUNT);
  }, [appointmentDetails?.data, patientGroupClassEvents?.data, patientId]);

  useEffect(() => {
    if (patientId) {
      // 90 mins is the longest duration of appointments
      const from = toISO(DateTime.now().minus({ minutes: 90 }));

      // fetch more appointments to consider already finished appointments in response
      const pageSize = OVERVIEW_TAB_APPOINTMENTS_COUNT * 3;

      getPatientGroupClassEvents({
        patientExternalId: patientId,
        startDateTime: from,
        endDateTime: null,
        pageSize,
      });
    }
  }, [patientId]);

  return (
    <>
      <PatientSurveys
        assessmentList={assessmentList}
        isError={isAssessmentError}
        isLoading={isAssessmentLoading}
        onSendEmail={onSendEmail}
        onUpdateAssessmentStatus={onUpdateAssessmentStatus}
        patientFirstName={patientFirstName}
        patientLastName={patientLastName}
        selectedSurveyResults={selectedSurveyResults}
        surveys={surveys}
        updateAssessmentState={updateAssessmentState}
      />
      <ChakraFlex alignItems="stretch">
        <ChakraFlex
          alignItems="stretch"
          direction="column"
          marginEnd="56px"
          width="70%"
        >
          <>
            <ChakraBox marginBottom="16px">
              <InsightsOverview patientId={patientId} />
            </ChakraBox>
            <ChakraBox marginBottom="16px">
              <SectionHeader
                action={{
                  linkTo: PatientTabsCollection[PatientTabs.TASKS].subPath,
                  patientId,
                  rightLinkText: 'View all tasks',
                }}
                badge={{
                  color: 'tertiary.marigold.base',
                  count: patientTasks?.length,
                  text: 'active',
                  type: 'pill',
                }}
                headerText="Tasks"
              />
              <PatientTasksOverview
                detailsTabId={detailsTabId}
                openTasks={openTasks}
                patientId={patientId}
              />
            </ChakraBox>
            <ChakraBox marginBottom="16px">
              <SectionHeader
                action={{
                  linkTo: `${
                    PatientTabsCollection[PatientTabs.APPOINTMENTS].subPath
                  }/${CalendarListTypeOption.DAILY_VIEW}${
                    upcomingEvents.length > 0
                      ? `?date=${DateTime.fromISO(upcomingEvents[0].startDateTime).toISODate()}`
                      : ''
                  }`,
                  patientId,
                  rightLinkText: 'View all events',
                }}
                headerText="Upcoming events"
              />
              <UpcomingEvents
                events={upcomingEvents}
                linkTo={PatientTabsCollection[PatientTabs.APPOINTMENTS].subPath}
                loading={
                  appointmentsLoading || isGetPatientGroupClassEventsLoading
                }
                patientId={patientId}
              />
            </ChakraBox>
          </>
        </ChakraFlex>
        <ChakraFlex alignItems="stretch" direction="column" width="30%">
          <PatientOverviewRightRail
            patientFirstName={patientFirstName}
            patientId={patientId}
            patientLastName={patientLastName}
            updateAssessmentState={updateAssessmentState}
          />
        </ChakraFlex>
      </ChakraFlex>
    </>
  );
};

export default PatientOverviewTabV1;
